// Dashboard – agregace KPI napříč nabídkami, objednávkami, fakturami, skladem
// a výrobními lístky. Jediný round-trip na /api/dashboard/summary.

import { icon } from './icons.js';

const API_BASE_URL =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'http://localhost:1234/api'
    : '/api';

const OFFER_STATUS_META = {
  draft:       { label: 'Rozpracovaná', badge: 'bg-gray-100 text-gray-700' },
  sent:        { label: 'Odeslaná',     badge: 'bg-blue-100 text-blue-800' },
  negotiation: { label: 'Vyjednávání',  badge: 'bg-amber-100 text-amber-800' },
  approved:    { label: 'Schválená',    badge: 'bg-emerald-100 text-emerald-800' },
  rejected:    { label: 'Odmítnutá',    badge: 'bg-red-100 text-red-800' },
  converted:   { label: 'Převedená',    badge: 'bg-indigo-100 text-indigo-800' }
};

const ORDER_STATUS_META = {
  received:    { label: 'Přijato',       badge: 'bg-blue-100 text-blue-800' },
  in_progress: { label: 'Ve výrobě',     badge: 'bg-amber-100 text-amber-800' },
  done:        { label: 'Hotovo',        badge: 'bg-emerald-100 text-emerald-800' },
  to_invoice:  { label: 'K fakturaci',   badge: 'bg-purple-100 text-purple-800' },
  invoiced:    { label: 'Fakturováno',   badge: 'bg-indigo-100 text-indigo-800' },
  delivered:   { label: 'Dodáno',        badge: 'bg-gray-100 text-gray-800' },
  cancelled:   { label: 'Storno',        badge: 'bg-red-100 text-red-800' }
};

const INVOICE_STATUS_META = {
  draft:          { label: 'Rozpracovaná',     badge: 'bg-gray-100 text-gray-700' },
  issued:         { label: 'Vystavena',        badge: 'bg-blue-100 text-blue-800' },
  partially_paid: { label: 'Částečně uhrazena', badge: 'bg-amber-100 text-amber-800' },
  paid:           { label: 'Uhrazena',         badge: 'bg-emerald-100 text-emerald-800' },
  cancelled:      { label: 'Stornována',       badge: 'bg-gray-100 text-gray-500' }
};

// Stavy zakázek relevantní pro výrobu (technické listy) – viz vyroba.js.
const VYROBA_STATUS_META = {
  received:    { label: 'Příprava',    badge: 'bg-blue-100 text-blue-800' },
  in_progress: { label: 'Ve výrobě',   badge: 'bg-amber-100 text-amber-800' },
  done:        { label: 'Hotovo',      badge: 'bg-emerald-100 text-emerald-800' },
  cancelled:   { label: 'Zrušeno',     badge: 'bg-red-100 text-red-800' }
};

export class DashboardManager {
  constructor() {
    this.data = null;
    this.loading = false;
    this.collapsed = { overdue: true, lowStock: true, inProgress: true };
  }

  // ---------- API ----------

  async showSection() {
    await this.loadAndRender();
  }

  async refresh() {
    if (this.loading) return;
    await this.loadAndRender({ background: true });
  }

  async loadAndRender({ background = false } = {}) {
    const container = document.getElementById('dashboard-section-content');
    if (!container) return;
    this.loading = true;
    const hasData = Boolean(this.data);
    if (!background || !hasData) {
      container.innerHTML = this.renderHeader() + this.renderLoading();
    }

    try {
      const res = await fetch(`${API_BASE_URL}/dashboard/summary`, { credentials: 'include' });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error || 'Nepodařilo se načíst dashboard');
      }
      this.data = json.data;
      this.loading = false;
      this.render();
    } catch (err) {
      this.loading = false;
      if (background && hasData) {
        console.warn('Dashboard auto-refresh selhal:', err);
        return;
      }
      this.renderError(err.message);
    }
  }

  // ---------- Render ----------

  renderHeader() {
    return `
      <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
        <div>
          <h2 class="text-2xl font-semibold text-gray-900 mb-1">Přehled</h2>
          <p class="text-gray-600 mb-0 text-sm">Klíčové ukazatele napříč obchodem, výrobou, fakturací a skladem.</p>
        </div>
      </div>
    `;
  }

  render() {
    const container = document.getElementById('dashboard-section-content');
    if (!container || !this.data) return;

    const d = this.data;
    container.innerHTML = `
      ${this.renderHeader()}
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        ${this.renderKpiCard({
          icon: 'document-text',
          title: 'Nabídky tento měsíc',
          value: String(d.offers.thisMonth),
          subtitle: `${d.offers.total} celkem · konverze ${this.formatPct(d.offers.conversionRate)}`,
          accent: 'blue'
        })}
        ${this.renderKpiCard({
          icon: 'clipboard-check',
          title: 'Aktivní objednávky',
          value: String(d.orders.active),
          subtitle: this.renderOrderBadges(d.orders.byStatus),
          accent: 'amber'
        })}
        ${this.renderKpiCard({
          icon: 'banknotes',
          title: 'Obrat tento měsíc',
          value: this.formatCZK(d.invoices.revenueThisMonth),
          subtitle: `${d.invoices.issuedThisMonth} vystavených`,
          accent: 'emerald'
        })}
        ${this.renderKpiCard({
          icon: 'receipt',
          title: 'Nezaplaceno',
          value: this.formatCZK(d.invoices.unpaidAmount),
          subtitle: this.renderOverdueBadge(d.invoices.overdueCount),
          accent: d.invoices.overdueCount > 0 ? 'red' : 'gray'
        })}
        ${this.renderKpiCard({
          icon: 'printer',
          title: 'Ve výrobě',
          value: String(d.vyroba.inProgress),
          subtitle: `${d.vyroba.queued} v přípravě, ${d.vyroba.readyToShip} k expedici`,
          accent: 'indigo'
        })}
        ${this.renderKpiCard({
          icon: 'swatch',
          title: 'Nízké zásoby',
          value: String(d.stock.lowStockCount),
          subtitle: d.stock.lowStockCount > 0
            ? `<span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-800">pod minimem</span>`
            : `<span class="text-xs text-gray-500">vše v normě</span>`,
          accent: d.stock.lowStockCount > 0 ? 'red' : 'gray'
        })}
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        ${this.renderOverdueSection(d.invoices)}
        ${this.renderLowStockSection(d.stock)}
      </div>

      <div class="mt-6">
        ${this.renderInProgressSection(d.vyroba)}
      </div>
    `;

    container.querySelectorAll('[data-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => this.toggleSection(btn.dataset.toggle));
    });
    container.querySelectorAll('[data-cta]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (window.sectionManager) window.sectionManager.showSection(btn.dataset.cta);
      });
    });
  }

  toggleSection(id) {
    if (!id || !(id in this.collapsed)) return;
    this.collapsed[id] = !this.collapsed[id];
    const body = document.querySelector(`[data-body="${id}"]`);
    const toggle = document.querySelector(`[data-toggle="${id}"]`);
    if (!body || !toggle) return;
    body.classList.toggle('hidden', this.collapsed[id]);
    const chevron = toggle.querySelector(`svg[data-chevron="${id}"]`);
    if (chevron) chevron.classList.toggle('rotate-180', !this.collapsed[id]);
  }

  renderKpiCard({ icon: iconName, title, value, subtitle, accent }) {
    const colors = {
      blue:    { bg: 'bg-blue-50',    text: 'text-blue-600',    accent: 'text-blue-900' },
      amber:   { bg: 'bg-amber-50',   text: 'text-amber-600',   accent: 'text-amber-900' },
      emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', accent: 'text-emerald-900' },
      red:     { bg: 'bg-red-50',     text: 'text-red-600',     accent: 'text-red-900' },
      indigo:  { bg: 'bg-indigo-50',  text: 'text-indigo-600',  accent: 'text-indigo-900' },
      gray:    { bg: 'bg-gray-50',    text: 'text-gray-500',    accent: 'text-gray-900' }
    };
    const c = colors[accent] || colors.gray;
    return `
      <div class="bg-white rounded-lg border border-gray-200 p-3 flex items-start gap-3 shadow-sm">
        <div class="flex-shrink-0 w-9 h-9 rounded-md ${c.bg} ${c.text} flex items-center justify-center">
          ${icon(iconName, 'w-5 h-5')}
        </div>
        <div class="min-w-0">
          <div class="text-[11px] uppercase tracking-wide text-gray-500 font-medium">${this.escape(title)}</div>
          <div class="text-base sm:text-lg font-semibold ${c.accent} truncate tabular-nums" title="${this.escape(String(value))}">${this.escape(String(value))}</div>
          <div class="text-xs text-gray-500">${subtitle || ''}</div>
        </div>
      </div>
    `;
  }

  renderOrderBadges(byStatus) {
    const order = ['received', 'in_progress', 'to_invoice'];
    const parts = [];
    for (const s of order) {
      const n = byStatus[s] || 0;
      if (n <= 0) continue;
      const m = ORDER_STATUS_META[s];
      parts.push(`<span class="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] ${m.badge}">${m.label}: ${n}</span>`);
    }
    return parts.length ? parts.join(' ') : '<span class="text-xs text-gray-500">žádné aktivní</span>';
  }

  renderOverdueBadge(count) {
    if (count > 0) {
      return `<span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-800">${count} po splatnosti</span>`;
    }
    return '<span class="text-xs text-gray-500">vše po splatnosti</span>';
  }

  renderOverdueSection(invoices) {
    const items = invoices.overdueInvoices || [];
    const emptyBody = `
      <div class="p-8 text-center">
        ${icon('check-circle', 'mx-auto h-10 w-10 text-emerald-400 mb-2')}
        <p class="text-sm text-gray-500">Žádné faktury po splatnosti.</p>
      </div>
    `;
    const tableBody = `
      <div class="overflow-x-auto">
        <table class="min-w-full text-sm">
          <thead>
            <tr class="text-left border-b border-gray-200 bg-gray-50/60">
              <th class="px-4 py-2.5 font-medium text-gray-600">Faktura</th>
              <th class="px-4 py-2.5 font-medium text-gray-600">Zákazník</th>
              <th class="px-4 py-2.5 font-medium text-gray-600 text-right">Částka</th>
              <th class="px-4 py-2.5 font-medium text-gray-600">Splatnost</th>
              <th class="px-4 py-2.5 font-medium text-gray-600">Stav</th>
            </tr>
          </thead>
          <tbody>
            ${items.map((inv) => {
              const m = INVOICE_STATUS_META[inv.status] || { label: inv.status, badge: 'bg-gray-100 text-gray-800' };
              return `
                <tr class="border-b border-gray-100 hover:bg-gray-50">
                  <td class="px-4 py-2.5 font-medium text-gray-900">${this.escape(inv.id)}</td>
                  <td class="px-4 py-2.5 text-gray-700">${this.escape(inv.customerName || '—')}</td>
                  <td class="px-4 py-2.5 text-right text-gray-900">${this.formatCZK(inv.totalPriceWithVat)}</td>
                  <td class="px-4 py-2.5 text-gray-600">${this.formatDate(inv.dueDate)}</td>
                  <td class="px-4 py-2.5"><span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs ${m.badge}">${this.escape(m.label)}</span></td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      </div>
    `;
    return this.renderCollapsibleCard({
      id: 'overdue',
      icon: 'exclamation-triangle',
      iconColor: 'text-red-500',
      title: 'Po splatnosti',
      items,
      countBadgeClass: 'bg-red-100 text-red-800',
      body: items.length ? tableBody : emptyBody,
      ctaLabel: 'Přejít na faktury',
      ctaSection: 'faktury'
    });
  }

  renderLowStockSection(stock) {
    const items = stock.lowStock || [];
    const emptyBody = `
      <div class="p-8 text-center">
        ${icon('check-circle', 'mx-auto h-10 w-10 text-emerald-400 mb-2')}
        <p class="text-sm text-gray-500">Všechny materiály nad minimem.</p>
      </div>
    `;
    const tableBody = `
      <div class="overflow-x-auto">
        <table class="min-w-full text-sm">
          <thead>
            <tr class="text-left border-b border-gray-200 bg-gray-50/60">
              <th class="px-4 py-2.5 font-medium text-gray-600">Materiál</th>
              <th class="px-4 py-2.5 font-medium text-gray-600">Kategorie</th>
              <th class="px-4 py-2.5 font-medium text-gray-600 text-right">Skladem</th>
              <th class="px-4 py-2.5 font-medium text-gray-600 text-right">Min</th>
              <th class="px-4 py-2.5 font-medium text-gray-600 text-right">Dostupné</th>
            </tr>
          </thead>
          <tbody>
            ${items.map((m) => {
              const low = m.skladem <= m.minStock;
              return `
                <tr class="border-b border-gray-100 hover:bg-gray-50">
                  <td class="px-4 py-2.5 text-gray-900">${this.escape(m.name)}</td>
                  <td class="px-4 py-2.5 text-gray-600">${this.escape(m.categoryId)}</td>
                  <td class="px-4 py-2.5 text-right ${low ? 'text-red-700 font-medium' : 'text-gray-700'}">${m.skladem}</td>
                  <td class="px-4 py-2.5 text-right text-gray-500">${m.minStock}</td>
                  <td class="px-4 py-2.5 text-right text-gray-700">${m.available}</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      </div>
    `;
    return this.renderCollapsibleCard({
      id: 'lowStock',
      icon: 'swatch',
      iconColor: 'text-amber-500',
      title: 'Nízké zásoby',
      items,
      countBadgeClass: 'bg-amber-100 text-amber-800',
      body: items.length ? tableBody : emptyBody,
      ctaLabel: 'Přejít na papíry',
      ctaSection: 'papiry'
    });
  }

  /**
   * Skládací karta dashboardu. Defaultně sbalená – ukazuje titulek, počet a CTA.
   * Rozbalením se ukáže tělo (tabulka). Prázdný stav (items.length === 0)
   * se ukazuje rozevřený bez CTA a bez šipky.
   */
  renderCollapsibleCard({ id, icon: iconName, iconColor, title, items, countBadgeClass, body, ctaLabel, ctaSection }) {
    const hasItems = Array.isArray(items) && items.length > 0;
    const isCollapsed = this.collapsed[id];
    const chevronClass = isCollapsed ? '' : 'rotate-180';
    const bodyHiddenClass = isCollapsed ? 'hidden' : '';

    return `
      <div class="bg-white rounded-lg shadow-sm border border-gray-200">
        <div class="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
          <button type="button" data-toggle="${id}"
            class="flex items-center gap-2 text-left flex-1 min-w-0 cursor-pointer hover:text-blue-600">
            ${icon('chevron-down', `w-4 h-4 text-gray-400 transition-transform duration-200 ${chevronClass}`, { 'data-chevron': id })}
            ${icon(iconName, `h-5 w-5 ${iconColor} flex-shrink-0`)}
            <h3 class="text-base font-semibold text-gray-900 truncate">${this.escape(title)}</h3>
            ${hasItems ? `<span class="text-xs px-2 py-0.5 rounded-full ${countBadgeClass}">${items.length}</span>` : ''}
          </button>
          <button type="button" data-cta="${ctaSection}"
            class="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-700 flex-shrink-0">
            <span class="hidden sm:inline">${this.escape(ctaLabel)}</span>
            ${icon('arrow-right', 'w-4 h-4 sm:ml-1')}
          </button>
        </div>
        <div data-body="${id}" class="${bodyHiddenClass}">
          ${body}
        </div>
      </div>
    `;
  }

  renderInProgressSection(vyroba) {
    const items = vyroba.inProgressOrders || [];
    const emptyBody = `
      <div class="p-8 text-center">
        ${icon('check-circle', 'mx-auto h-10 w-10 text-emerald-400 mb-2')}
        <p class="text-sm text-gray-500">Žádné zakázky aktuálně ve výrobě.</p>
      </div>
    `;
    const tableBody = `
      <div class="overflow-x-auto">
        <table class="min-w-full text-sm">
          <thead>
            <tr class="text-left border-b border-gray-200 bg-gray-50/60">
              <th class="px-4 py-2.5 font-medium text-gray-600">Zakázka</th>
              <th class="px-4 py-2.5 font-medium text-gray-600">Zákazník</th>
              <th class="px-4 py-2.5 font-medium text-gray-600">Poslední změna</th>
              <th class="px-4 py-2.5 font-medium text-gray-600">Stav</th>
              <th class="px-4 py-2.5 font-medium text-gray-600 text-right">Cena</th>
            </tr>
          </thead>
          <tbody>
            ${items.map((t) => {
              const m = VYROBA_STATUS_META[t.status] || { label: t.status, badge: 'bg-gray-100 text-gray-800' };
              return `
                <tr class="border-b border-gray-100 hover:bg-gray-50">
                  <td class="px-4 py-2.5 font-medium text-gray-900">${this.escape(t.id)}</td>
                  <td class="px-4 py-2.5 text-gray-700">${this.escape(t.customerName || '—')}</td>
                  <td class="px-4 py-2.5 text-gray-600">${this.formatDate(t.updatedAt)}</td>
                  <td class="px-4 py-2.5"><span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs ${m.badge}">${this.escape(m.label)}</span></td>
                  <td class="px-4 py-2.5 text-right text-gray-700">${(t.totalPrice || 0).toLocaleString('cs-CZ')} Kč</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      </div>
    `;
    return this.renderCollapsibleCard({
      id: 'inProgress',
      icon: 'printer',
      iconColor: 'text-indigo-500',
      title: 'Ve výrobě',
      items,
      countBadgeClass: 'bg-indigo-100 text-indigo-800',
      body: items.length ? tableBody : emptyBody,
      ctaLabel: 'Přejít na technické listy',
      ctaSection: 'vyroba'
    });
  }

  renderLoading() {
    return `
      <div class="flex justify-center p-12">
        ${icon('spinner', 'animate-spin h-8 w-8 text-blue-500')}
      </div>
    `;
  }

  renderError(message) {
    const container = document.getElementById('dashboard-section-content');
    if (!container) return;
    container.innerHTML = `
      ${this.renderHeader()}
      <div class="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-700">${this.escape(message)}</div>
    `;
  }

  // ---------- Helpers ----------

  formatCZK(n) {
    const num = Number(n) || 0;
    return num.toLocaleString('cs-CZ', { maximumFractionDigits: 0 }) + ' Kč';
  }

  formatPct(n) {
    const num = Number(n) || 0;
    return num.toLocaleString('cs-CZ', { maximumFractionDigits: 1 }) + ' %';
  }

  formatDate(value) {
    if (!value) return '—';
    const d = (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value))
      ? new Date(value.slice(0, 4), parseInt(value.slice(5, 7), 10) - 1, value.slice(8, 10))
      : new Date(value);
    if (Number.isNaN(d.getTime())) return this.escape(String(value));
    return d.toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  notify(msg, type = 'success') {
    if (typeof window.showNotification === 'function') window.showNotification(msg, type);
  }

  escape(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

window.DashboardManager = DashboardManager;
